import type Backbone from 'backbone';

import { isExtBankCurrentAccount, refreshAccountBalance } from '@retail/products-core';
import { INSTANCE_NAMES, getInstance, setInstance } from '@retail/single-instance-cache';
import { logger } from '@luna/logger';
import { request } from '@luna/api';
import { createQueryString } from '@luna/router';
import { georgeStats } from '@shared/statistics';
import { triggerGlobalEvent } from '@luna/events';
import { isFeatureEnabled } from '@luna/gatekeeper';
import { getEnv } from '@luna/env';
import { getToken } from '@shared/auth';
import { invalidateTransactions } from '@shared/transactions-api';
import type { RetailEnvironmentConfig } from '@retail/core-config';
import { playSound } from '@retail/core';

import type { MyStateResponseTimeStamp } from './TransactionCheckHelper';
import { TransactionCheckHelper } from './TransactionCheckHelper';

type MyStateResponse = {
    accounts: {
        [accountId: string]: MyStateResponseTimeStamp;
    };
};

export function getHelper(): TransactionCheckHelper {
    let helper = getInstance<TransactionCheckHelper>(INSTANCE_NAMES.TRANSACTION_CHECK_HELPER);

    if (!helper) {
        helper = new TransactionCheckHelper();
        setInstance(INSTANCE_NAMES.TRANSACTION_CHECK_HELPER, helper);
    }

    return helper;
}

async function ensureLatestAccountTransactions(): Promise<void> {
    const helper = getHelper();

    if (helper.latestAccountTransactions !== null) {
        return;
    }

    try {
        const results: MyStateResponse = await request(`${getEnv().georgeApiUrl}my/state`);

        helper.latestAccountTransactions = results.accounts;
    } catch (e) {
        logger.error(e);
    }
}

function reloadTransactions(accountId: string): void {
    const account = getInstance<Backbone.Collection>(INSTANCE_NAMES.LEGACY_PRODUCT_COLLECTION).get(
        accountId,
    );

    if (account) {
        triggerGlobalEvent('transactions:reload', accountId);
    }
}

function reloadAccount(accountId: string): void {
    const account = getInstance<Backbone.Collection>(INSTANCE_NAMES.LEGACY_PRODUCT_COLLECTION).get(
        accountId,
    );

    if (!account) {
        return;
    }

    if (isExtBankCurrentAccount(account.attributes)) {
        // multibanking accounts use the my_state update for other reasons than balance updates.
        account.fetch();
        return;
    }

    refreshAccountBalance(accountId, {
        onSuccess: (count) => {
            georgeStats.trackEvent('BALANCE_REFRESH_MY_STATE', 'BALANCE_REFRESH_MY_STATE_SUCCESS', {
                count,
                accountId,
            });
        },
        onFail: (count) => {
            georgeStats.trackEvent('BALANCE_REFRESH_MY_STATE', 'BALANCE_REFRESH_MY_STATE_FAIL', {
                count,
                accountId,
            });
        },
    });
}

/**
 * A recent Transaction is any that was recorded within the time of the
 * `my/state` service's configured caching window. This is currently set to 2
 * hours.
 *
 * The service responds with an object of IDs for Accounts with recent
 * Transactions mapped against the timestamp of the latest recent Transaction
 * for the Account.
 */
type LatestTransactionTimes = {
    [accountId: string]: string;
};

function getAccountsWithNewTransactions(
    currentTimes: LatestTransactionTimes,
    previousTimes: LatestTransactionTimes,
): Array<string> {
    const accountIdsWithTimes = Object.keys(currentTimes).filter((accountId) =>
        Boolean(currentTimes[accountId]),
    );

    return accountIdsWithTimes.filter((accountId) => {
        const previous = previousTimes[accountId];
        const current = currentTimes[accountId];
        return previous !== current;
    });
}

function reloadAccountsForNewTransactions(currentTimes: LatestTransactionTimes): void {
    const helper = getHelper();
    const { latestAccountTransactions: previousTimes } = helper;
    const accountsWithNewTransactions = getAccountsWithNewTransactions(currentTimes, previousTimes);
    const hasNewTransactions = accountsWithNewTransactions.length > 0;

    if (hasNewTransactions) {
        invalidateTransactions();
    }

    accountsWithNewTransactions.forEach((accountId) => {
        reloadTransactions(accountId);
        reloadAccount(accountId);
    });

    if (hasNewTransactions) {
        playSound('tx-in');
        triggerGlobalEvent('shell:myStateTriggered', accountsWithNewTransactions);
    }

    helper.latestAccountTransactions = currentTimes;
}

async function checkForNewTransactions(): Promise<void> {
    await ensureLatestAccountTransactions();

    const helper = getHelper();

    if (helper.isChecking) {
        return;
    }

    helper.isChecking = true;

    try {
        const results: MyStateResponse = await request(
            `${getEnv<RetailEnvironmentConfig>().georgeApiUrl}my/state`,
        );

        if (results.accounts) {
            reloadAccountsForNewTransactions(results.accounts);
        }

        helper.isChecking = false;
    } catch {
        helper.isChecking = false;
    }
}

export async function longPollForNewTransactions(accountId: string): Promise<void> {
    if (!isFeatureEnabled((f) => f.longPollAfterPayment)) {
        return;
    }
    await ensureLatestAccountTransactions();

    const helper = getHelper();
    const lastTransactionTimestamp: string = helper.latestAccountTransactions[accountId];

    const queryParams = {
        longPolling: 'true',
        accountId,
        ...(!!lastTransactionTimestamp && {
            minLastTransactionTimestamp: lastTransactionTimestamp,
        }),
    };

    try {
        const { georgeApiUrl } = getEnv<RetailEnvironmentConfig>();
        const url = `${georgeApiUrl}my/state${createQueryString(queryParams)}`;
        const results = await request<MyStateResponse>(url);

        const newLastTransactionTimestamp = results.accounts[accountId];
        if (
            newLastTransactionTimestamp &&
            newLastTransactionTimestamp !== lastTransactionTimestamp
        ) {
            helper.latestAccountTransactions[accountId] = newLastTransactionTimestamp;
            invalidateTransactions();
            reloadTransactions(accountId);
            reloadAccount(accountId);
            playSound('tx-in');
            triggerGlobalEvent('shell:myStateTriggered', Object.keys(results.accounts));
            return;
        }

        // fetch account to update balance
        // even if no new transaction has been imported yet
        reloadAccount(accountId);
    } catch (e) {
        logger.error(e);
    }
}

export function startCheckingForNewTransactions(): void {
    const helper = getHelper();
    const NEW_TRANSACTION_CHECK_INTERVAL = 15000;

    if (!helper.syncCheckId) {
        helper.syncCheckId = setInterval(() => {
            if (getToken()) {
                checkForNewTransactions();
            }
        }, NEW_TRANSACTION_CHECK_INTERVAL);
    }
}
